import {apiGet} from './client'
import {isOccupation, jobFromEvent, jobLabel} from './jobs'
import type {EventRaw, Family, Person, PlaceNode, TreeIndex} from './types'

/** Тянем всё древо целиком одним заходом. pagesize с запасом:
 *  67 персон сейчас, но и на пару тысяч это один запрос и меньше секунды. */
export async function loadTree(): Promise<TreeIndex> {
  const [people, families, events, places, notes, media] = await Promise.all([
    apiGet<Person[]>('/people/', {profile: 'self', locale: 'ru', pagesize: 20000}),
    apiGet<Family[]>('/families/', {pagesize: 20000}),
    apiGet<EventRaw[]>('/events/', {pagesize: 20000}),
    apiGet<RawPlace[]>('/places/', {pagesize: 20000}),
    apiGet<RawNote[]>('/notes/', {pagesize: 20000}),
    apiGet<RawMedia[]>('/media/', {pagesize: 20000}),
  ])
  return buildIndex(people, families, events, places, notes, media)
}

interface RawMedia {
  handle: string
  mime?: string
  date?: {dateval?: number[]}
}

/** Дата снимка одним числом для сортировки: ггггммдд. Ноль — даты нет,
 *  такие уходят в конец: неизвестное время не должно рвать хронологию. */
export const mediaSortKey = (dv?: number[]): number => {
  const y = dv?.[2] || 0
  return y ? y * 10000 + (dv?.[1] || 0) * 100 + (dv?.[0] || 0) : 0
}

interface RawNote {
  handle: string
  text?: {string?: string}
}

export function buildIndex(
  people: Person[],
  families: Family[],
  events: EventRaw[] = [],
  places: RawPlace[] = [],
  notes: RawNote[] = [],
  media: RawMedia[] = [],
): TreeIndex {
  const pMap = new Map(people.map(p => [p.handle, p]))
  const fMap = new Map(families.map(f => [f.handle, f]))

  const famsOfPartner = new Map<string, Family[]>()
  const famOfChild = new Map<string, Family>()

  for (const f of families) {
    for (const h of [f.father_handle, f.mother_handle]) {
      if (!h) continue
      const list = famsOfPartner.get(h)
      if (list) list.push(f)
      else famsOfPartner.set(h, [f])
    }
    for (const c of f.child_ref_list || []) famOfChild.set(c.ref, f)
  }

  return {
    people: pMap,
    families: fMap,
    events: new Map(events.map(e => [e.handle, e])),
    places: new Map(places.map(p => [p.handle, placeNode(p)])),
    notes: new Map(notes.map(n => [n.handle, (n.text?.string || '').trim()])),
    media: new Map(media.map(m =>
      [m.handle, {mime: m.mime || '', sort: mediaSortKey(m.date?.dateval)}])),
    famsOfPartner,
    famOfChild,
    components: components(pMap, families),
  }
}

/** Древо может быть не одно: в реальных данных встречаются несвязанные острова.
 *  Без этого списка до второго куска из интерфейса просто не доехать. */
function components(people: Map<string, Person>, families: Family[]): string[][] {
  const adj = new Map<string, Set<string>>()
  const link = (a: string, b: string) => {
    let s = adj.get(a)
    if (!s) adj.set(a, (s = new Set()))
    s.add(b)
  }
  for (const f of families) {
    const members = [f.father_handle, f.mother_handle, ...(f.child_ref_list || []).map(c => c.ref)]
      .filter((h): h is string => !!h)
    for (const a of members) for (const b of members) if (a !== b) link(a, b)
  }

  const seen = new Set<string>()
  const out: string[][] = []
  for (const h of people.keys()) {
    if (seen.has(h)) continue
    const stack = [h]
    const comp: string[] = []
    seen.add(h)
    while (stack.length) {
      const x = stack.pop()!
      comp.push(x)
      for (const y of adj.get(x) || []) if (!seen.has(y)) (seen.add(y), stack.push(y))
    }
    out.push(comp)
  }
  return out.sort((a, b) => b.length - a.length)
}

export interface RawPlace {
  handle: string
  name?: {value?: string}
  title?: string
  place_type?: string | {string?: string}
  placeref_list?: {ref?: string}[]
  lat?: string
  long?: string
}

const placeNode = (p: RawPlace): PlaceNode => ({
  // title после разбора иерархии пуст — короткое имя лежит в name.value
  name: (p.name?.value || p.title || "").trim(),
  parent: p.placeref_list?.[0]?.ref || null,
  type: typeof p.place_type === "string" ? p.place_type : p.place_type?.string || "",
  lat: (p.lat || "").trim(),
  long: (p.long || "").trim(),
})

/** Полное название по цепочке родителей: «ул. Красных Партизан, Мерефа, Украина».
 *  Ограничение глубины — защита от кольца, если кто-то закольцует вложенность. */
export function placePath(ix: TreeIndex, handle: string | null | undefined): string {
  const out: string[] = []
  let h = handle || null
  for (let i = 0; h && i < 10; i++) {
    const n = ix.places.get(h)
    if (!n) break
    if (n.name) out.push(n.name)
    h = n.parent
  }
  return out.join(", ")
}

/** Прямые потомки места (null — корневые). */
export const placeChildren = (ix: TreeIndex, parent: string | null): string[] =>
  [...ix.places.entries()].filter(([, n]) => n.parent === parent).map(([h]) => h)

/* ---------- производные от индекса ---------- */

export const parentsOf = (ix: TreeIndex, h: string): string[] => {
  const f = ix.famOfChild.get(h)
  return f ? [f.father_handle, f.mother_handle].filter((x): x is string => !!x) : []
}

export const famsOf = (ix: TreeIndex, h: string): Family[] => ix.famsOfPartner.get(h) || []

export const spouseOf = (ix: TreeIndex, h: string): string | null => {
  const f = famsOf(ix, h)[0]
  if (!f) return null
  const other = f.father_handle === h ? f.mother_handle : f.father_handle
  return other || null
}

export const kidsOf = (ix: TreeIndex, h: string): string[] =>
  famsOf(ix, h).flatMap(f => (f.child_ref_list || []).map(c => c.ref))

export const sibsOf = (ix: TreeIndex, h: string): string[] => {
  const f = ix.famOfChild.get(h)
  return f ? (f.child_ref_list || []).map(c => c.ref).filter(x => x !== h) : []
}

/* ---------- отображение ---------- */

/**
 * Фамилия для показа: «Воронько (Мягкова)».
 *
 * В базе основным именем у всех 32 женщин стоит имя ПРИ РОЖДЕНИИ — так
 * пришёл импорт, и так правильно: род определяется по девичьей фамилии,
 * на ней держится и группировка в штатном Gramps, и наш отбор по роду.
 * Но в списках человека ищут по той фамилии, под которой знают, — по
 * замужней. Поэтому местами их не меняем, а показываем обе: замужнюю
 * впереди, девичью в скобках.
 */
export function surnameOf(p: Person | undefined): string {
  const birth = p?.profile?.name_surname || ''
  const married = marriedSurname(p)
  return married ? `${married} (${birth})` : birth
}

/** Фамилия из имени «В браке», если она отличается от основной. */
function marriedSurname(p: Person | undefined): string {
  const birth = (p?.profile?.name_surname || '').trim()
  for (const n of p?.alternate_names || []) {
    const t = n.type
    const isMarried = typeof t === 'string' ? t === 'Married Name' : t?.value === 3
    if (!isMarried) continue
    const s = (n.surname_list?.[0]?.surname || '').trim()
    if (s && s !== birth) return s
  }
  return ''
}

/** Имя по частям — для карточки древа, где строки верстаются отдельно. */
export function nameParts(p: Person | undefined): {main: string; alt: string; given: string} {
  const birth = p?.profile?.name_surname || ''
  const married = marriedSurname(p)
  return {
    main: married || birth,
    alt: married ? birth : '',
    // в first_name лежат имя и отчество вместе — так их положил импорт
    given: p?.profile?.name_given || '',
  }
}

/** Фамилия + имя. Отчество в карточку не влезает — оно на странице человека. */
export function shortName(p: Person | undefined): string {
  if (!p) return '—'
  const pr = p.profile
  if (!pr) return '—'
  const given = (pr.name_given || '').split(' ')[0]
  return [surnameOf(p), given].filter(Boolean).join(' ') || pr.name_display || '—'
}

export function fullName(p: Person | undefined): string {
  if (!p?.profile) return shortName(p)
  // готовое name_display берём, только когда показывать нечего сверх него:
  // в нём фамилия одна, а нам нужны обе
  if (!marriedSurname(p)) return p.profile.name_display || shortName(p)
  return [surnameOf(p), p.profile.name_given].filter(Boolean).join(', ')
}

/** `date` в профиле — локализованная строка («14 марта 1932»), не ISO.
 *  Поэтому год достаём регуляркой, а не Date.parse. */
export const yearOf = (e?: {date?: string}): string => {
  const m = (e?.date || '').match(/\d{4}/)
  return m ? m[0] : ''
}

/** У почти половины людей дат нет вовсе — тогда честно ничего не пишем.
 *  Одинокий год рождения показываем без «р.»: это и так самый частый
 *  случай. А вот «ум.» оставляем — без него одинокий год читался бы
 *  как год рождения, и это была бы прямая дезинформация. */
export function lifespan(p: Person | undefined): string {
  const b = yearOf(p?.profile?.birth)
  const d = yearOf(p?.profile?.death)
  if (b && d) return `${b} – ${d}`
  if (b) return b
  if (d) return `ум. ${d}`
  return ''
}

/** Точная дата события человека по типу. В профиле дата приходит
 *  локализованной строкой, а тут нужны число и месяц — берём из события. */
function evDateval(ix: TreeIndex, p: Person | undefined, type: string): number[] | null {
  for (const r of p?.event_ref_list || []) {
    const e = ix.events.get(r.ref)
    if (e?.type === type && e.date?.dateval?.[2]) return e.date.dateval as number[]
  }
  return null
}

/** Сколько полных лет прошло между датами. Неизвестные число и месяц —
 *  нули: тогда считаем по годам, а результат помечаем как приблизительный. */
function yearsBetween(a: number[], b: number[]): {years: number; exact: boolean} {
  const [ad, am, ay] = a
  const [bd, bm, by] = b
  const exact = !!(ad && am && bd && bm)
  let years = by - ay
  if (exact && (bm < am || (bm === am && bd < ad))) years -= 1
  return {years, exact}
}

export const YEARS_RU = (n: number): string => {
  const t = n % 100
  if (t >= 11 && t <= 14) return 'лет'
  switch (n % 10) {
    case 1: return 'год'
    case 2: case 3: case 4: return 'года'
    default: return 'лет'
  }
}

/* Дольше этого не живут. Отсутствие события смерти означает «неизвестно»,
   а не «жив»: у стариков из начала века его просто никто не завёл, и без
   этой границы им набегало бы по полтора века. */
const MAX_AGE = 110

/**
 * Возраст: у живых — на сегодня, у ушедших — на день смерти.
 *
 * Точность честная: если у даты известен только год, возраст может
 * разойтись на год, и мы ставим «~». Врать про «84 года» там, где
 * известно только «1932», нельзя — это родословная, а не прикидка.
 */
/** Год рождения человека числом. 0 — неизвестен. */
export const birthYear = (p: Person | undefined): number =>
  Number(yearOf(p?.profile?.birth)) || 0

/**
 * Сколько человеку было в таком-то году.
 *
 * Считаем по годам: у снимков дата почти всегда «1979», а не полная, и
 * точнее года всё равно не выйдет. Поэтому и подписываем «около».
 * Отрицательное — снимок раньше рождения, значит где-то ошибка; такое
 * не показываем, а не показываем «-3 года».
 */
export function ageInYear(p: Person | undefined, year: number): number | null {
  const b = birthYear(p)
  if (!b || !year) return null
  const age = year - b
  return age >= 0 && age <= 120 ? age : null
}

/** Год по возрасту: обратная задача, когда известен возраст на снимке. */
export function yearByAge(p: Person | undefined, age: number): number | null {
  const b = birthYear(p)
  if (!b || !Number.isFinite(age) || age < 0 || age > 120) return null
  return b + age
}

export function ageOf(ix: TreeIndex, p: Person | undefined, today = new Date()): string {
  const birth = evDateval(ix, p, 'Birth')
  if (!birth) return ''
  const death = evDateval(ix, p, 'Death')
  const gone = isDeceased(p)

  // человека нет, а когда — неизвестно: возраст не посчитать
  if (gone && !death) return ''

  const to = death || [today.getDate(), today.getMonth() + 1, today.getFullYear()]
  const {years, exact} = yearsBetween(birth, to)
  if (years < 0 || (!gone && years > MAX_AGE)) return ''
  return `${exact ? '' : '~'}${years} ${YEARS_RU(years)}`
}

/**
 * Поиск по имени, устойчивый к порядку слов и знакам.
 *
 * Gramps отдаёт готовое имя как «Воронько, Виктор Мефодьевич» — с запятой
 * и фамилией впереди. Сравнение строки целиком требовало вводить запятую
 * и угадывать порядок: «Виктор Воронько» не находился.
 *
 * Поэтому ищем по словам: каждое слово запроса должно встретиться где-то
 * в имени, в любом порядке. Знаки препинания выкидываем с обеих сторон,
 * ё приравниваем к е — в базе одна и та же фамилия встречается и так, и так.
 */
const norm = (s: string) => s.toLowerCase().replace(/ё/g, 'е').replace(/[^a-zа-я0-9]+/gi, ' ').trim()

export const searchWords = (q: string): string[] => norm(q).split(' ').filter(Boolean)

export function nameMatches(p: Person | undefined, words: string[]): boolean {
  if (!words.length) return false
  const pr = p?.profile
  if (!pr) return false
  // замужнюю фамилию тоже берём в поиск: человека часто ищут именно по
  // ней, а в основном имени стоит девичья
  const hay = norm(
    [pr.name_surname, marriedSurname(p), pr.name_given, pr.name_display]
      .filter(Boolean).join(' '),
  )
  return words.every(w => hay.includes(w))
}

/** То же для мест: «Красных Партизан Мерефа» должно находить улицу,
 *  хотя в полном названии между ними запятая. */
export function pathMatches(path: string, words: string[]): boolean {
  if (!words.length) return false
  const hay = norm(path)
  return words.every(w => hay.includes(w))
}

export const isFemale = (p: Person | undefined): boolean => p?.profile?.sex === 'F'

export const initials = (p: Person | undefined): string => {
  const pr = p?.profile
  if (!pr) return '?'
  return ((pr.name_surname?.[0] || '') + (pr.name_given?.[0] || '')).toUpperCase() || '?'
}

export const photoOf = (p: Person | undefined): string | null => p?.media_list?.[0]?.ref || null

/** Какую часть главного снимка показывать. null — весь снимок. */
export const photoRect = (p: Person | undefined): number[] | null => {
  const r = p?.media_list?.[0]?.rect
  return r && r.length === 4 ? r : null
}

/** Адрес миниатюры главного снимка с учётом выбранной области. */
export function portraitUrl(p: Person | undefined, size: number, jwt: string): string | null {
  const h = photoOf(p)
  if (!h) return null
  const r = photoRect(p)
  const base = r
    ? `/api/media/${h}/cropped/${r[0]}/${r[1]}/${r[2]}/${r[3]}/thumbnail/${size}`
    : `/api/media/${h}/thumbnail/${size}`
  return `${base}?jwt=${jwt}`
}

/** Места работы человека — из событий типа Occupation, привязанных к нему. */
export function jobsOf(ix: TreeIndex, h: string): string[] {
  const p = ix.people.get(h)
  if (!p?.event_ref_list?.length) return []
  return p.event_ref_list
    .map(r => ix.events.get(r.ref))
    .filter((e): e is EventRaw => !!e && isOccupation(e.type))
    .map(e => jobLabel(jobFromEvent(e as never)))
    .filter(Boolean)
}

/** Человек считается ушедшим, если у него есть событие смерти.
 *  По дате ориентироваться нельзя: у 18 из 67 событие есть, а дата пустая.
 *  Отсутствие события НЕ означает «жив» — означает «неизвестно», и это
 *  осознанно осторожная сторона: траурная лента на живом хуже, чем её
 *  отсутствие у умершего. */
export const isDeceased = (p: Person | undefined): boolean => !!p?.profile?.death?.type

/** Места жительства человека — события Residence с привязанным местом.
 *  Мест рождения в данных нет ни у кого, а проживание заполнено у 23 из 76,
 *  так что «Место» в списке — это именно оно.
 *  Тип сверяем по строке: GET всегда отдаёт имя типа, а не объект. */
export function placesOf(ix: TreeIndex, h: string): string[] {
  const p = ix.people.get(h)
  if (!p?.event_ref_list?.length) return []
  const out: string[] = []
  for (const r of p.event_ref_list) {
    const e = ix.events.get(r.ref)
    if (!e || e.type !== 'Residence' || !e.place) continue
    const title = placePath(ix, e.place)
    if (title && !out.includes(title)) out.push(title)
  }
  return out
}

/** Первая непустая заметка человека — для строки в списке людей. */
export function firstNote(ix: TreeIndex, h: string): string {
  for (const nh of ix.people.get(h)?.note_list || []) {
    const t = ix.notes.get(nh)
    if (t) return t
  }
  return ''
}

/**
 * Корень рода: женская форма фамилии приводится к мужской.
 * Мягкова -> Мягков, Ихонкина -> Ихонкин, Задорожная -> Задорожный.
 *
 * Нужно для группировки по родам: без этого одна семья распадается на две
 * записи, и «все Дубинины» показывают то четверых мужчин, то девять женщин.
 *
 * Трогаем только характерные русские окончания. Фамилии на -о, -к, -ук
 * (Борщенко, Воронько, Просяник) не склоняются по роду и остаются как есть —
 * поэтому правило по ним не проходит вовсе.
 */
export function surnameRoot(surname: string): string {
  const s = surname.trim()
  if (s.length < 4) return s
  const end = (suffix: string) => s.toLowerCase().endsWith(suffix)

  if (end('ская')) return s.slice(0, -4) + 'ский'
  if (end('цкая')) return s.slice(0, -4) + 'цкий'
  if (end('ная')) return s.slice(0, -3) + 'ный'
  if (end('ая')) return s.slice(0, -2) + 'ый'
  if (end('ова') || end('ева') || end('ёва')) return s.slice(0, -1)
  if (end('ина') || end('ына')) return s.slice(0, -1)
  return s
}
